import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { defaultCustomersSidebar, resolveSidebar, type AnySidebar, type CustomersSidebar, type Sidebar } from "@omnabase/shared";
import { api } from "./api";
import { useSession } from "../shell/store";
import { createSidebarSync, type SidebarSync } from "./sidebarSync";

/** The sidebar and consent round (brief rules "The column" and "The dialog"; question 5): the person's inbox column in this tab. The layout
 *  arrives with the workspace (`me.sidebar`) before the column draws, so there is no browser copy; a change draws at once (the column follows
 *  the dialog live) and is saved a short moment later, one request for a burst of changes. Done, Escape and leaving the page send a pending
 *  save at once. Kept per workspace for the life of the tab.
 *  The sidebar editor rework (2026-10-26): the rules of when the edited copy gives way to the workspace's live in `sidebarSync.ts`; this hook
 *  only connects them to React and the network.
 *  The customers sidebar round (2026-10-29): the Customers column the same way (`me.customersSidebar`, saved as `customersSidebar`), one sync
 *  per workspace and column, read over the customer tags this tab knows (a colleague's new tag shows as soon as the tags load). */

const SAVE_AFTER_MS = 400;
type Area = "inbox" | "customers";
const FIELD: Record<Area, "sidebar" | "customersSidebar"> = { inbox: "sidebar", customers: "customersSidebar" };
const syncs = new Map<string, SidebarSync<AnySidebar>>();
/** the customer tags each workspace's Customers column is read over (the newest the tab loaded) */
const customerTagsOf = new Map<string, readonly { id: string; name: string; kind?: string }[]>();
const readCustomers = (slug: string) => (stored: unknown) => resolveSidebar(stored, defaultCustomersSidebar(customerTagsOf.get(slug) ?? []));
function syncFor(slug: string, area: Area): SidebarSync<AnySidebar> {
  const key = `${area}:${slug}`;
  let s = syncs.get(key);
  if (!s) {
    s = createSidebarSync<AnySidebar>({ delayMs: SAVE_AFTER_MS, save: (body, keepalive) => api.patch(`/api/v1/w/${slug}/me/settings`, { [FIELD[area]]: body }, { keepalive }), ...(area === "customers" ? { resolve: readCustomers(slug) } : {}) });
    syncs.set(key, s);
  }
  return s;
}
/** every pending save, at once (the page goes away) */
const flushAll = () => { for (const s of syncs.values()) void s.flush(true); };
if (typeof window !== "undefined") window.addEventListener("pagehide", flushAll);

export type SidebarStore<L extends AnySidebar = Sidebar> = {
  layout: L;
  /** draw this layout now and save it a moment later */
  change: (next: L) => void;
  /** "Reset to default": today's column again, the stored layout forgotten */
  reset: () => void;
  /** send a pending save now (Done, Escape) */
  flush: () => void;
  /** goes up after every save: the counts follow the entries the column shows */
  saved: number;
};
function useAreaSidebar<L extends AnySidebar>(area: Area, server: L, read: (l: L) => L = (l) => l): SidebarStore<L> {
  const { workspace } = useSession();
  const sync = useMemo(() => syncFor(workspace.slug, area), [workspace.slug, area]) as unknown as SidebarSync<L>;
  useSyncExternalStore(sync.subscribe, sync.version);
  const layout = read(sync.shown(server));
  const change = useCallback((next: L) => sync.change(next), [sync]);
  const reset = useCallback(() => sync.reset(), [sync]);
  const flush = useCallback(() => void sync.flush(), [sync]);
  useEffect(() => () => void sync.flush(), [sync]); // leaving the screen sends what waits
  return { layout, change, reset, flush, saved: sync.saved() };
}
/** the inbox's column */
export function useSidebar(): SidebarStore {
  const { workspace } = useSession();
  return useAreaSidebar("inbox", workspace.me.sidebar);
}
/** the Customers column, read over the customer tags the screen loaded (`tags`; null while they load) */
export function useCustomersSidebar(tags: readonly { id: string; name: string; kind?: string }[] | null): SidebarStore<CustomersSidebar> {
  const { workspace } = useSession();
  const slug = workspace.slug;
  if (tags) customerTagsOf.set(slug, tags.filter((t) => t.kind === "customer"));
  const read = useCallback((l: CustomersSidebar) => (tags ? resolveSidebar(l, defaultCustomersSidebar(customerTagsOf.get(slug) ?? [])) : l), [tags, slug]);
  return useAreaSidebar("customers", workspace.me.customersSidebar, read);
}
